import { Country, Region } from '../hooks/locationsHooks';
import { Icons } from '../icons';
import { testId } from '../utils';
import {
  DEFAULT_PHONE_LENGTH,
  PHONE_DIGIT_MAX,
  PHONE_DIGIT_MIN,
} from '../utils/constants';
import { Checkbox, Input, InputType, Select, Typography } from './primitives';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TextField,
} from './ui/form';
import { useTranslation } from '@rpi/i18n';
import React, { PropsWithChildren } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

interface AddressFormData extends FieldValues {
  firstName: string;
  lastName: string;
  company?: string;
  phone: string;
  countryIso: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  regionIsoCode: string;
  postalCode: string;
  defaultShipping: boolean;
  poBoxApo: boolean;
}

type AddressFormProps = {
  form: UseFormReturn<AddressFormData>;
  countries: Country[];
  regions: Region[];
  regionLabel?: string;
  loading?: boolean;
  onSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  noDefault?: boolean;
};

export const transformPhoneInput = (inputValue: string) => {
  const value = inputValue.replace(/\s/g, '');
  const digits = value.replace(/[^0-9]/g, '');

  if (digits.length > PHONE_DIGIT_MAX) {
    // Per the international standard E.164, phone numbers cannot be
    // longer than 15 digits including country code
    // https://en.wikipedia.org/wiki/E.164
    return inputValue;
  }

  if (digits.length < PHONE_DIGIT_MIN) {
    // Just using a reasonable min for now. Returning value instead of
    // inputValue so that whitespace will be removed
    return value;
  }

  if (/[^-+0-9()]/g.test(value)) {
    // Non-phone characters found
    return inputValue;
  }

  if (digits.length === DEFAULT_PHONE_LENGTH && value[0] !== '+') {
    // Standard US phone number
    return digits.replace(/([0-9]{3})([0-9]{3})([0-9]{4})/, '$1-$2-$3');
  } else if (digits.length === DEFAULT_PHONE_LENGTH + 1 && value[0] === '+') {
    // Single digit country code happy path
    return digits.replace(
      /([0-9])([0-9]{3})([0-9]{3})([0-9]{4})/,
      '+$1 $2-$3-$4',
    );
  }

  // Returning value so that whitespace will be removed for any strange
  // but valid phone number
  return value;
};

export const AddressForm: React.FC<PropsWithChildren<AddressFormProps>> = ({
  form,
  onSubmit,
  countries,
  regions,
  regionLabel,
  loading,
  children,
  noDefault,
}) => {
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form noValidate className="flex flex-col gap-4" onSubmit={onSubmit}>
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <FormField
            disabled={loading}
            control={form.control}
            name={'firstName'}
            render={({ field, fieldState }) => (
              <TextField
                required
                {...testId('firstname')}
                className="flex-1"
                label={t('route.user.settings.account.first_name')}
                invalid={fieldState.invalid}
                {...field}
              />
            )}
          />
          <FormField
            disabled={loading}
            control={form.control}
            name={'lastName'}
            render={({ field, fieldState }) => (
              <TextField
                required
                {...testId('lastname')}
                className="flex-1"
                label={t('route.user.settings.account.last_name')}
                invalid={fieldState.invalid}
                {...field}
              />
            )}
          />
        </div>
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <FormField
            disabled={loading}
            control={form.control}
            name={'company'}
            render={({ field, fieldState }) => (
              <TextField
                className="flex-1"
                {...testId('company')}
                label={t('route.user.addresses.company')}
                invalid={fieldState.invalid}
                {...field}
              />
            )}
          />
          <FormField
            disabled={loading}
            control={form.control}
            name={'phone'}
            render={({ field, fieldState }) => (
              <FormItem className="flex-1">
                <FormLabel {...testId('phone', 'label')}>
                  <Typography variant="form-label">
                    {t('route.user.addresses.phone')} <Icons.Indicator />
                  </Typography>
                </FormLabel>
                <FormControl>
                  <Input
                    {...testId('phone')}
                    type={InputType.PHONE}
                    placeholder=""
                    {...field}
                    value={field.value}
                    onBlur={(e) => {
                      field.onChange(transformPhoneInput(e.target.value));
                      field.onBlur();
                    }}
                    invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage {...testId('phone', 'message')} />
              </FormItem>
            )}
          />
        </div>
        <FormField
          disabled={loading}
          control={form.control}
          name={'countryIso'}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel {...testId('country', 'select', 'label')}>
                <Typography variant="form-label">
                  {t('route.user.addresses.country')} <Icons.Indicator />
                </Typography>
              </FormLabel>
              <FormControl>
                <Select
                  {...testId('country', 'select')}
                  value={field.value}
                  onChange={field.onChange}
                  data={countries}
                  invalid={fieldState.invalid}
                  placeholder={t('route.checkout.select_an_option') || ''}
                />
              </FormControl>
              <FormMessage {...testId('country', 'select', 'message')} />
            </FormItem>
          )}
        />
        <FormField
          disabled={loading}
          control={form.control}
          name={'addressLine1'}
          render={({ field, fieldState }) => (
            <TextField
              required
              {...testId('addressLine1')}
              label={t('route.user.addresses.address1')}
              invalid={fieldState.invalid}
              {...field}
            />
          )}
        />
        <FormField
          disabled={loading}
          control={form.control}
          name={'poBoxApo'}
          render={({ field }) => (
            // Need !important because FormItem assumes flex-col
            <FormItem className="flex !flex-row gap-1 items-center">
              <FormControl>
                <Checkbox
                  {...testId('poBoxApo')}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel {...testId('poBoxApo', 'label')}>
                <Typography variant="body">
                  {t('route.user.addresses.po_box')}
                </Typography>
              </FormLabel>
            </FormItem>
          )}
        />
        <FormField
          disabled={loading}
          control={form.control}
          name={'city'}
          render={({ field, fieldState }) => (
            <TextField
              required
              {...testId('city')}
              label={t('route.user.addresses.city')}
              invalid={fieldState.invalid}
              {...field}
            />
          )}
        />
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          <FormField
            disabled={loading}
            control={form.control}
            name={'regionIsoCode'}
            render={({ field, fieldState }) => (
              <FormItem className="flex-1">
                <FormLabel {...testId('region', 'label')}>
                  <Typography variant="form-label">
                    {regionLabel || t('route.user.addresses.region')}{' '}
                    <Icons.Indicator />
                  </Typography>
                </FormLabel>
                <FormControl>
                  {regions && regions.length > 0 ? (
                    <Select
                      {...testId('region', 'select')}
                      value={field.value}
                      onChange={field.onChange}
                      data={regions}
                      invalid={fieldState.invalid}
                      placeholder={
                        t('route.user.addresses.placeholder_region') || ''
                      }
                    />
                  ) : (
                    <Input
                      {...testId('region', 'textinput')}
                      type={InputType.TEXT}
                      {...field}
                      invalid={fieldState.invalid}
                    />
                  )}
                </FormControl>
                <FormMessage {...testId('region', 'message')} />
              </FormItem>
            )}
          />
          <FormField
            disabled={loading}
            control={form.control}
            name={'postalCode'}
            render={({ field, fieldState }) => (
              <TextField
                {...testId('postalcode')}
                className="flex-1"
                required
                label={t('route.user.addresses.zip')}
                invalid={fieldState.invalid}
                {...field}
              />
            )}
          />
        </div>
        {!noDefault && (
          <FormField
            disabled={loading}
            control={form.control}
            name={'defaultShipping'}
            render={({ field }) => (
              // Need !important because FormItem assumes flex-col
              <FormItem className="flex !flex-row gap-1 items-center">
                <FormControl>
                  <Checkbox
                    {...testId('default-shipping')}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel {...testId('default-shipping', 'label')}>
                  <Typography variant="body">
                    {t('route.user.addresses.set_default')}
                  </Typography>
                </FormLabel>
              </FormItem>
            )}
          />
        )}
        {children}
      </form>
    </Form>
  );
};
